/* ============================================================================
 * vp-hero.js — RESTORE180 PDP hero (task 02-hero)
 * Two custom elements, multi-instance safe, vanilla ES6+, no jQuery.
 *
 *  <vp-hero-tabs>  — accordion group (Program Details / Supplement Facts /
 *                    Suggested Use). All content lives in the DOM at all times
 *                    (SEO/AEO); JS only reorders so the open tab sits at the
 *                    top, matching designer note 86:13664. Native <details>.
 *
 *  <vp-hero-gallery> — the gallery video is now rendered server-side in
 *                    snippets/product-thumbnail.liquid as a vp-lightbox trigger
 *                    button (data-vp-lightbox="video"), gated by
 *                    enable_vp_redesign. vp-lightbox.js delegates the click, so
 *                    no JS rewiring is needed here (annotation 86:13676). This
 *                    element remains as a styling/scope hook + safety net that
 *                    upgrades any stray inline deferred-media video into a
 *                    lightbox trigger. Image zoom keeps the product-media-modal.
 * ========================================================================== */
(function () {
  'use strict';

  if (!window.customElements) return;

  /* ------------------------------------------------------------------ tabs */
  class VPHeroTabs extends HTMLElement {
    connectedCallback() {
      // Desktop: horizontal tab buttons + panels.
      this._btns   = Array.from(this.querySelectorAll('.vp-hero__tab-btn'));
      this._panels = Array.from(this.querySelectorAll('.vp-hero__tab-panel'));
      this._btns.forEach((btn) => {
        btn.addEventListener('click', () => this._activateDesktopTab(Number(btn.dataset.vpTab)));
      });

      // Mobile: single-open <details> accordion.
      this._tabs = Array.from(this.querySelectorAll('.vp-hero__tab'));
      this._tabs.forEach((tab) => {
        tab.addEventListener('toggle', () => this._onTabToggle(tab));
      });
    }

    _activateDesktopTab(idx) {
      this._btns.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === idx);
        btn.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
      this._panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === idx);
      });
    }

    _onTabToggle(tab) {
      if (!tab.open) {
        const s = tab.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', 'false');
        return;
      }
      // Close all others
      this._tabs.forEach((t) => {
        if (t !== tab) {
          t.open = false;
          const s = t.querySelector('summary');
          if (s) s.setAttribute('aria-expanded', 'false');
        }
      });
      const s = tab.querySelector('summary');
      if (s) {
        s.setAttribute('aria-expanded', 'true');
        const stickyHeader = document.querySelector('sticky-header, .shopify-section-header-sticky, header.shopify-section');
        const headerH = stickyHeader ? stickyHeader.offsetHeight : 0;
        const top = s.getBoundingClientRect().top + window.scrollY - headerH - 10;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }

  /* --------------------------------------------------------------- gallery */
  class VPHeroGallery extends HTMLElement {
    connectedCallback() {
      // Find the theme's deferred-media video posters in this gallery and
      // re-route their click to the shared lightbox instead of inline play.
      this._rewireVideos();
    }

    _rewireVideos() {
      // Safety net only: in the redesign the video item is rendered in liquid
      // as a vp-lightbox trigger and no <deferred-media> is emitted. If any
      // stray inline deferred-media video survives (e.g. a duplicate gallery
      // instance), replace it with a poster button that opens the lightbox.
      const deferreds = this.querySelectorAll('deferred-media.media, deferred-media');
      deferreds.forEach((deferred) => {
        const url = this._videoUrl(deferred);
        if (!url) return;
        const posterImg = deferred.querySelector('.deferred-media__poster img, img');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vp-hero__video-poster';
        btn.setAttribute('data-vp-lightbox', 'video');
        btn.setAttribute('data-video-url', url);
        const play = document.createElement('span');
        play.className = 'vp-hero__video-play';
        play.setAttribute('aria-hidden', 'true');
        btn.appendChild(play);
        if (posterImg) btn.appendChild(posterImg.cloneNode(true));
        const container = deferred.closest('.product-media-container') || deferred.parentNode;
        // Remove the theme's modal-opener + deferred-media so only the trigger
        // remains (vp-lightbox.js delegates the click from document).
        const opener = container.querySelector('modal-opener');
        if (opener) opener.remove();
        deferred.replaceWith(btn);
      });
    }

    _videoUrl(deferred) {
      // Prefer an explicit data attribute (set in liquid), else read the
      // <template> source/iframe the theme rendered.
      if (deferred.dataset && deferred.dataset.vpVideoUrl) return deferred.dataset.vpVideoUrl;
      const tpl = deferred.querySelector('template');
      if (!tpl) return null;
      const frag = tpl.content.cloneNode(true);
      const iframe = frag.querySelector('iframe');
      if (iframe && iframe.src) return iframe.src;
      const source = frag.querySelector('video source, source');
      if (source && source.src) return source.src;
      const video = frag.querySelector('video');
      if (video && video.src) return video.src;
      return null;
    }
  }

  /* --------------------------------------------------------------- sticky buy box */
  function initStickyBuyBox() {
    const stickyEls = document.querySelectorAll('.vp-redesign product-info.product__column-sticky');
    if (!stickyEls.length) return;

    const updateTop = (el) => {
      const elHeight = el.offsetHeight;
      const vh = window.innerHeight;
      let bottomPadding = 20; // default 20px from bottom
      const stickyAtc = document.querySelector('.vp-sticky-atc__inner');
      if (stickyAtc && window.getComputedStyle(stickyAtc).display !== 'none') {
        bottomPadding += stickyAtc.offsetHeight;
      }
      const offset = vh - elHeight - bottomPadding;
      const topVal = Math.min(20, offset); // 20px from top if short
      el.style.setProperty('--vp-sticky-top', `${topVal}px`);
    };

    const ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        updateTop(entry.target);
      }
    });

    stickyEls.forEach(el => {
      ro.observe(el);
      updateTop(el);
    });

    window.addEventListener('resize', () => {
      stickyEls.forEach(el => updateTop(el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyBuyBox);
  } else {
    initStickyBuyBox();
  }

  if (!customElements.get('vp-hero-tabs')) customElements.define('vp-hero-tabs', VPHeroTabs);
  if (!customElements.get('vp-hero-gallery')) customElements.define('vp-hero-gallery', VPHeroGallery);
})();
